import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride, { TooltipRenderProps, Step, CallBackProps, STATUS } from 'react-joyride';
import { createStyles, withStyles, Theme, WithTheme, WithStyles } from '@material-ui/core/styles';
import { Button, Card, CardActions, Grid, Typography } from '@material-ui/core';
import store from 'store';

import { useScrollDisable } from 'hooks';

interface IProps extends WithTheme, WithStyles<typeof styles> {
  steps: Step[];
  storageKey: string;
}

const styles = (theme: Theme) =>
  createStyles({
    card: {
      border: 'none',
    },
    controls: {
      padding: theme.spacing(2),
    },
    progress: {
      color: theme.palette.grey[600],
    },
    button: {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.grey[600],
    },
  });

const WALKTHROUGH_KEY = 'walkthrough';

const Walkthrough: FC<IProps> = ({ classes, theme, steps, storageKey }) => {
  const [run, setRun] = useState(false);
  const { t } = useTranslation();
  const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];

  useScrollDisable(WALKTHROUGH_KEY, run);

  useEffect(() => {
    if (!!store.get(storageKey)) {
      return undefined;
    }

    events.forEach(event => {
      window.addEventListener(event, initWalkthrough);
    });

    return () => cleanUpWalkthrough();
  });

  function initWalkthrough() {
    if (!run) {
      setRun(true);
    }
  }

  function cleanUpWalkthrough() {
    events.forEach(event => {
      window.removeEventListener(event, initWalkthrough);
    });
  }

  const JoyrideTooltip: FC<TooltipRenderProps> = props => {
    const { step, primaryProps, skipProps, tooltipProps, index, size } = props;
    const { onClick, title } = primaryProps;

    return (
      <div ref={tooltipProps.ref}>
        <Card className={classes.card}>
          {step.content}
          <CardActions className={classes.controls}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography className={classes.progress}>
                  {t('components:indexOf', { index: index + 1, size })}
                </Typography>
              </Grid>
              <Grid item>
                <Button className={classes.button} onClick={skipProps.onClick}>
                  {t('common:skip')}
                </Button>
              </Grid>
              <Grid item xs container justify="flex-end">
                <Button color="secondary" variant="contained" size="large" onClick={onClick}>
                  {t(title)}
                </Button>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </div>
    );
  };

  function handleJoyrideCallback(callback: CallBackProps) {
    if (callback.status === STATUS.FINISHED || callback.status === STATUS.SKIPPED) {
      store.set(storageKey, 1);
      setRun(false);
      cleanUpWalkthrough();
    }
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      tooltipComponent={JoyrideTooltip}
      callback={handleJoyrideCallback}
      disableScrolling={true}
      disableOverlayClose={true}
      disableCloseOnEsc={true}
      styles={{
        options: {
          zIndex: theme.zIndex.modal,
        },
      }}
    />
  );
};

export default withStyles(styles, { withTheme: true })(Walkthrough);
